<template>
	<div class="page">
		<v-card
			class="sgnl-mx-auto github-auth-card"
			width="95%"
			max-width="700px"
		>
			<div class="sgnl-text-center sgnl-subtitle-medium sgnl-text-color-default">
				Connecting to your GitHub account
			</div>
			<v-slide-y-transition
				group
				tag="div"
				class="sgnl-flex sgnl-flex-column sgnl-gap-16"
			>
				<div
					v-for="(authTask, i) in authTasks"
					:key="`${i}-${authTask.text}`"
					class="sgnl-flex sgnl-align-center sgnl-gap-16"
				>
					<div
						class="sgnl-flex-grow-1"
						:class="taskColor(authTask)"
						v-text="authTask.text"
					/>
					<v-scale-transition hide-on-leave>
						<Checkmark16
							v-if="authTask.done && authTask.success"
							:class="taskColor(authTask)"
						/>
						<Close16
							v-else-if="authTask.done && !authTask.success"
							:class="taskColor(authTask)"
						/>
						<v-progress-circular
							v-else
							:width="3"
							color="var(--sgnl-brand-background)"
						/>
					</v-scale-transition>
				</div>
			</v-slide-y-transition>
			<v-expand-transition>
				<AlertMessage
					:show-alert="showError"
					type="error"
				>
					{{ errorMessage }}
				</AlertMessage>
			</v-expand-transition>
		</v-card>
	</div>
</template>

<script>
import { defineComponent } from "vue";
import { mockTimeout } from "@/js/githubClient";
import { useGithubStore } from "@/stores/github";
import { monitoringCaptureError } from "@/plugins/monitoring";
import AlertMessage from "@/components/Common/AlertMessage.vue";
import Checkmark16 from "@carbon/icons-vue/es/checkmark/16";
import Close16 from "@carbon/icons-vue/es/close/16";

// @ts-ignore FIXME: add correct types
// type AuthTask = {
// 	done: boolean;
// 	success: boolean;
// 	text: string;
// };
// type ComponentData = {
// 	oauthRequestState: undefined | string | null;
// 	alertColour: string;
// 	showError: boolean;
// 	errorMessage: string;
// 	authTasks: AuthTask[];
// };

export default defineComponent({
	name: "GithubAuth",
	components: { AlertMessage, Checkmark16, Close16 },
	props: ["code", "connect", "state", "error", "errorDescription"],
	data: () => ({
		oauthRequestState: undefined,
		alertColour: "info",
		showError: false,
		errorMessage: "",
		authTasks: [],
	}),
	setup() {
		const githubStore = useGithubStore();
		return { githubStore };
	},
	computed: {},
	methods: {
		taskColor(authTask) {
			return authTask.done && !authTask.success ? "sgnl-text-color-error" : "sgnl-text-color-default";
		},
		async validateAuthRequestAndLogin() {
			this.oauthRequestState = sessionStorage.getItem("gh-login-state");
			try {
				if (this.state == this.oauthRequestState) {
					this.authTasks.at(-1).done = true;
					this.authTasks.at(-1).success = true;
					sessionStorage.removeItem("gh-login-state");

					// check if authorised
					this.authTasks.push({
						done: false,
						success: false,
						text: "Authorizing with GitHub",
					});

					if (this.error) {
						this.authTasks.at(-1).done = true;
						this.authTasks.at(-1).success = false;
						throw new Error(this.errorDescription);
					} else {
						this.authTasks.at(-1).done = true;
						this.authTasks.at(-1).success = true;

						// get tokens
						this.authTasks.push({
							done: false,
							success: false,
							text: "Logging into the GitHub account",
						});

						this.authTasks.at(-1).done = true;
						this.authTasks.at(-1).success = true;

						this.authTasks.push({
							done: false,
							success: false,
							text: "Fetching GitHub account details",
						});
						await this.githubStore.githubLogin(this.code);
						this.authTasks.at(-1).done = true;
						this.authTasks.at(-1).success = true;

						this.alertColour = "success";
					}
				} else {
					sessionStorage.removeItem("gh-login-state");
					throw new Error("Unexpected request from GitHub. Please try again.");
				}
			} catch (error) {
				monitoringCaptureError(error, "Github OAuth login");
				this.$posthog?.capture("github_auth_failed", {
					error: this.error,
					error_description: this.errorDescription,
				});
				this.authTasks.at(-1).done = true;
				this.authTasks.at(-1).success = false;
				this.showError = true;
				this.errorMessage = error;
				this.alertColour = "error";
				await this.githubStore.githubLogout();
			} finally {
				this.authTasks.push({
					done: false,
					success: false,
					text: "Redirecting to Repositories",
				});

				const mockTimeoutInMs = this.showError ? 5000 : 2000;

				const isRedirectionToConnectRepositoryAvailable = this.connect != undefined && this.connect.length > 0;

				mockTimeout(mockTimeoutInMs).then(() => {
					isRedirectionToConnectRepositoryAvailable
						? this.$router.push({ name: "Repositories", query: { connect: this.connect } })
						: this.$router.push({ name: "Repositories" });
				});
			}
		},
	},
	mounted() {
		this.authTasks.push({
			done: false,
			success: false,
			text: "Communicating with GitHub",
		});
		mockTimeout(500).then(() => {
			this.validateAuthRequestAndLogin();
		});
	},
});
</script>

<style scoped>
.github-auth-card {
	background-color: var(--sgnl-default-primary-background);
	border: none;
	box-shadow: none;
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-16);
	padding: var(--sgnl-space-24);
}
</style>
